import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Camera, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Profile photo upload dialog.
 *
 * Why no in-browser cropper?
 *   The user constraint is "Shadcn/UI only — no new libraries." A square crop
 *   UI requires a third-party React cropper. Instead, the BACKEND center-crops
 *   the image to a square 512x512 JPEG via Pillow on upload (see
 *   _process_photo in server.py). Users see a preview pre-crop and the final
 *   square version after upload.
 */
const PhotoUploadDialog = ({ open, onOpenChange, onUpdated }) => {
  const { api } = useAuth();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setError('');
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError('Photo is too large (max 5 MB).');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Photo must be a JPG, PNG, or WebP.');
      return;
    }
    setError('');
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/users/me/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile photo saved.');
      onUpdated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || "We couldn't upload that photo. Try another?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="w-[calc(100%-2rem)] sm:max-w-md bg-card-cream border-0 shadow-xl p-5 sm:p-6"
        data-testid="photo-upload-dialog"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-3">
            <Camera className="h-7 w-7 text-amber-600" />
          </div>
          <DialogTitle className="text-lg sm:text-xl font-heading font-bold text-slate-900">
            Update your photo
          </DialogTitle>
          <DialogDescription className="text-slate-600 mt-2 leading-relaxed">
            JPG, PNG, or WebP. Max 5 MB. We'll crop it square so it looks good everywhere.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onPick}
            className="hidden"
            data-testid="photo-file-input"
          />

          {previewUrl ? (
            <div className="flex justify-center">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-40 h-40 object-cover rounded-2xl border-4 border-white shadow-soft"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full h-32 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 text-slate-500 hover:border-amber-400 hover:text-amber-700 transition-colors"
              data-testid="photo-pick-btn"
            >
              <Upload className="h-6 w-6" />
              <span className="text-sm font-medium">Choose a photo</span>
            </button>
          )}

          {previewUrl && (
            <Button
              type="button"
              variant="ghost"
              className="w-full text-sm text-slate-600"
              onClick={() => inputRef.current?.click()}
            >
              Pick a different photo
            </Button>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button
            type="button"
            disabled={!file || busy}
            onClick={submit}
            className="w-full btn-primary py-6 text-base"
            data-testid="photo-save-btn"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save photo'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoUploadDialog;
